/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **Which headers carry a credential, and what each answer is used for.** Two policies, declared side
 * by side so neither is a second answer to the other's question (`#1313`, `#1323`).
 *
 * | Policy | Question | Match | Consumers |
 * | --- | --- | --- | --- |
 * | {@link isSensitiveHeaderName} | *might this value be a secret?* | substring, broad | capture masking, and the path resolver's refusal |
 * | {@link isNeverForwardedHeaderName} | *is this inbound header a credential for **us**?* | exact, narrow | the outbound header merge |
 * | {@link NOT_FORWARDED_BY_A_PROXY} | *does this inbound header describe our hop, or the player's body?* | exact | the outbound header merge |
 * | {@link suggestsSecretValue} | *should a row this name default to secret?* | the broad policy | the mapping editors, as a **suggestion** (`CR2`, `#1371`) |
 *
 * **The broad one errs toward caution, and both its consumers can afford that.** Masking too much
 * costs nothing, and the resolver refusing `x-session-id` only stops it being mapped onto a query
 * parameter — it is still forwarded as a header. The resolver deliberately stays on this policy: the
 * outbound `headers` root carries a source's **own** authored credentials (`x-api-key`), and an exact
 * list would let a mapping copy one into a URL, where nothing masks it.
 *
 * **The narrow one decides what reaches a partner, so it must not be broad.** Every inbound header is
 * forwarded unless bidBrain owns the call (CLAUDE.md's drop rule), and `cookie` is forwarded on
 * purpose, as part of the drop-in expectation (Todd, 2026-09-14).
 */

/**
 * Patterns for {@link isSensitiveHeaderName}, matched case-insensitively as a substring, so
 * `x-my-api-key` and `Proxy-Authorization` are covered.
 */
export const SENSITIVE_HEADER_PATTERNS: readonly string[] = [
  'authorization',
  'cookie',
  'auth',
  'token',
  'key',
  'secret',
  'password',
  'session',
];

/** Whether a header's value might be a secret, by {@link SENSITIVE_HEADER_PATTERNS}. */
export function isSensitiveHeaderName(name: string): boolean {
  const lower = String(name).toLowerCase();
  return SENSITIVE_HEADER_PATTERNS.some((pattern) => lower.includes(pattern));
}

/**
 * **Whether a row this name should start out declared secret** (`AUTHORED_CREDENTIALS_AT_REST_DESIGN.md`
 * `CR2`, `#1371`). A **suggestion, never a decision**: the row carries `secret`, and this only seeds it
 * where the operator has not said. The broad policy is right for a suggestion for the reason it is
 * wrong for a decision — encrypting a harmless value costs a value the operator can no longer read
 * back, so an over-inclusive guess is offered with the box ticked and can be unticked.
 *
 * The same answer on every plane, a header, a query parameter or a body leaf, so moving a credential
 * between them does not move it out of the suggestion.
 */
export function suggestsSecretValue(name: string): boolean {
  return isSensitiveHeaderName(name);
}

/**
 * **What a secret reads as once it is stored** (`CR4`). The server hands this back in place of the
 * value, and a save that posts it back keeps the stored value. It was declared twice across the alias
 * boundary, in `libs/config` and in `ConfigPage.tsx`, with nothing holding the two equal (`#1198`'s
 * shape); it lives here so every reader compares against one declaration.
 */
export const CONFIG_SECRET_PLACEHOLDER = '__YB_CONFIG_SECRET_SET__';

/**
 * **Inbound headers bidBrain owns the call on, so they never reach a demand source** (`#1323`, option
 * 1, Todd 2026-09-14). Whatever a player sends in these authenticates it to us, or to a CDN or gateway
 * in front of us. Forwarding one hands one party's credential to several others. A partner's own
 * credential is authored on the source, and that layer wins the merge anyway.
 */
export const NEVER_FORWARDED_HEADER_NAMES: readonly string[] = Object.freeze(['authorization', 'proxy-authorization']);

/** Whether an **inbound** header must be dropped from every outbound request. Exact, case-insensitive. */
export function isNeverForwardedHeaderName(name: string): boolean {
  return NEVER_FORWARDED_HEADER_NAMES.includes(String(name).toLowerCase());
}

/**
 * **Inbound headers a proxy does not forward, whatever they say** (`#1353`, slice `1h`). They describe
 * the player's connection to us, or the body the player sent us, and neither is the request we send.
 *
 * | Header | Why it is not ours to send | Measured in req-loop's Node 22 fetch |
 * | --- | --- | --- |
 * | `host` | names *our* address | ignored, while the request log claimed we sent it |
 * | `content-length` | frames the inbound body | shorter than our body hangs to the timeout; longer fails |
 * | `content-type`, `content-encoding` | describe the inbound body | a POST's `content-type` is comma-joined onto ours |
 * | `transfer-encoding`, `keep-alive`, `upgrade` | hop-by-hop (RFC 9110 §7.6.1) | **the request fails** (`UND_ERR_INVALID_ARG`) |
 * | `expect` | asks *us* for a 100 Continue | **the request fails** (`UND_ERR_NOT_SUPPORTED`) |
 * | `connection`, `proxy-connection`, `te`, `trailer` | hop-by-hop | sent, describing a connection that is not this one |
 * | `accept-encoding` | names the **player's** decoders, and we are the client that decodes | `zstd`, which browsers send, came back **undecodable** |
 *
 * Any name the inbound `Connection` header lists is hop-by-hop too, by the same RFC, and is dropped
 * with it. Not a credential rule: {@link NEVER_FORWARDED_HEADER_NAMES} answers a different question,
 * and a header on this list is still masked or not by {@link isSensitiveHeaderName}.
 */
export const NOT_FORWARDED_BY_A_PROXY: readonly string[] = Object.freeze([
  'host',
  'content-length',
  'content-type',
  'content-encoding',
  'transfer-encoding',
  'connection',
  'keep-alive',
  'proxy-connection',
  'te',
  'trailer',
  'upgrade',
  'expect',
  'accept-encoding',
]);

/** The names an inbound `Connection` header marks as hop-by-hop, lowercased. */
function connectionListedNames(headers: Record<string, string>): Set<string> {
  const listed = new Set<string>();
  for (const [name, value] of Object.entries(headers)) {
    if (name.toLowerCase() !== 'connection') continue;
    for (const token of String(value).split(',')) {
      const trimmed = token.trim().toLowerCase();
      if (trimmed) listed.add(trimmed);
    }
  }
  return listed;
}

/**
 * **The inbound headers a demand source may receive**: all of them, less the credentials
 * {@link isNeverForwardedHeaderName} names and the connection and framing headers
 * {@link NOT_FORWARDED_BY_A_PROXY} names.
 *
 * Applied at **both** places inbound headers enter a request, because each route can carry them to
 * the wire on its own:
 * - `BaseDriver.buildRequestHeaders`, the merge;
 * - `RequestDecoratorPipeline.buildContext`, which seeds a decorator context's headers. That layer
 *   merges **last**, so a filter at the merge alone let `Authorization` back in on every source with
 *   a decorator pipeline. Found in slice `1b`'s rehearsal, after a spec with no pipeline had passed.
 *
 * A header a decorator writes itself is authored, and is not filtered.
 */
export function forwardableInboundHeaders(headers: Record<string, string> | undefined): Record<string, string> {
  const source = headers ?? {};
  const hopByHop = connectionListedNames(source);
  const out: Record<string, string> = {};
  for (const [name, value] of Object.entries(source)) {
    const lower = name.toLowerCase();
    if (isNeverForwardedHeaderName(lower) || NOT_FORWARDED_BY_A_PROXY.includes(lower) || hopByHop.has(lower)) {
      continue;
    }
    out[name] = value;
  }
  return out;
}

/**
 * **Merge header layers, lowest precedence first, comparing names case-insensitively** (`#1353`).
 *
 * HTTP names are case-insensitive and a plain object is not. Spread together, an inbound
 * `user-agent` and the driver's `User-Agent` were two keys, and fetch sent both joined:
 * `user-agent: Roku/DVP, Roku/DVP`, and an `X-Forwarded-For` that **appended** where the rule says
 * it sets. So a higher layer's name replaces every spelling of it below, and keeps its own spelling.
 */
export function mergeHeaderLayers(
  ...layers: ReadonlyArray<Record<string, string> | undefined>
): Record<string, string> {
  const out: Record<string, string> = {};
  const spelling = new Map<string, string>();
  for (const layer of layers) {
    for (const [name, value] of Object.entries(layer ?? {})) {
      const lower = name.toLowerCase();
      const previous = spelling.get(lower);
      if (previous !== undefined) delete out[previous];
      spelling.set(lower, name);
      out[name] = value;
    }
  }
  return out;
}
